/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Factory, Handshake, Download, ChevronRight } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { ASSETS, METRICS, PILLARS, PROJECTS, MILESTONES, PARTNERS } from '../data/content';
import { ScrollReveal, Stagger, StaggerItem } from '../components/Motion';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';

const IconMap = { Compass, Factory, Handshake };

const Home = () => {
    const { t } = useLang();

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen flex items-end bg-navy-deep overflow-hidden grain-overlay">
                <div className="absolute inset-0">
                    <img src={ASSETS.heroCover} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/70 to-navy-deep/30" />
                    <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/85 via-navy-deep/40 to-transparent" />
                </div>

                <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 md:pb-32 pt-32">
                    <ScrollReveal>
                        <div className="eyebrow text-gold mb-8">{t('hero.eyebrow')}</div>
                    </ScrollReveal>
                    <ScrollReveal delay={0.1}>
                        <h1 className="editorial-h1 text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl">
                            {t('hero.h1')}
                        </h1>
                    </ScrollReveal>
                    <ScrollReveal delay={0.2}>
                        <p className="mt-8 text-white/80 text-base md:text-lg max-w-2xl leading-relaxed">
                            {t('hero.sub')}
                        </p>
                    </ScrollReveal>
                    <ScrollReveal delay={0.3}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/projects"
                                data-testid="hero-cta-projects"
                                className="group inline-flex items-center gap-3 px-7 py-3.5 bg-gold text-navy-deep text-[13px] tracking-widest uppercase font-medium hover:bg-gold-light transition-colors"
                            >
                                {t('common.explore_projects')}
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a
                                href="https://customer-assets.emergentagent.com/job_energy-innovation-sa/artifacts/i3ejzg8i_DELTA%20International%20Energy_Company_Profile_v03.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                data-testid="hero-cta-download"
                                className="inline-flex items-center gap-3 px-7 py-3.5 border border-white/30 text-white text-[13px] tracking-widest uppercase hover:border-gold hover:text-gold transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                {t('common.download_profile')}
                            </a>
                        </div>
                    </ScrollReveal>
                </div>

                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-[10px] tracking-[0.4em] uppercase animate-pulse">
                    Scroll
                </div>
            </section>

            {/* METRICS STRIP */}
            <section data-testid="metrics-strip" className="bg-navy-deep border-y border-gold/20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-y-10 md:gap-y-0 md:divide-x divide-gold/15">
                        {METRICS.map((m, i) => (
                            <ScrollReveal key={i} delay={i * 0.08} className="md:px-8 first:md:pl-0 last:md:pr-0">
                                <div className="text-center md:text-left">
                                    <div className="metric-number text-3xl md:text-5xl">
                                        {m.value}
                                        {m.unit && <span className="text-base ml-1 text-gold/80">{m.unit}</span>}
                                    </div>
                                    <div className="mt-3 text-white/60 text-[11px] tracking-[0.18em] uppercase leading-snug">
                                        {t(`metrics.${m.key}`)}
                                    </div>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO WE ARE */}
            <section className="bg-cream py-24 md:py-36">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                        <div className="lg:col-span-5">
                            <ScrollReveal>
                                <div className="eyebrow mb-6">{t('home.who_eyebrow')}</div>
                                <h2 className="editorial-h2 text-navy text-3xl md:text-5xl">
                                    {t('home.who_h2')}
                                </h2>
                            </ScrollReveal>
                        </div>
                        <div className="lg:col-span-7">
                            <ScrollReveal delay={0.1}>
                                <p className="text-ink/80 text-base md:text-lg leading-relaxed">
                                    {t('home.who_body')}
                                </p>
                                <figure className="mt-12 border-l-2 border-gold pl-8">
                                    <blockquote className="font-display italic text-navy text-xl md:text-2xl leading-snug">
                                        {t('home.who_quote')}
                                    </blockquote>
                                    <figcaption className="mt-5 text-ink/60 text-sm">
                                        {t('home.who_attrib')}
                                    </figcaption>
                                </figure>
                                <Link to="/about" data-testid="link-read-story" className="mt-10 inline-flex items-center gap-2 text-navy text-sm tracking-widest uppercase link-underline">
                                    {t('common.read_story')} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </ScrollReveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="bg-navy-deep py-24 md:py-32 relative grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="text-center max-w-2xl mx-auto mb-20">
                            <div className="eyebrow mb-5">{t('home.pillars_eyebrow')}</div>
                            <h2 className="editorial-h2 text-white text-3xl md:text-5xl">
                                {t('home.pillars_h2')}
                            </h2>
                        </div>
                    </ScrollReveal>
                    <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/15">
                        {PILLARS.map((p) => {
                            const Icon = IconMap[p.icon] || Compass;
                            return (
                                <StaggerItem key={p.n} testid={`pillar-${p.n}`} className="bg-navy-deep p-10 md:p-12 group hover:bg-navy-mid transition-colors">
                                    <div className="flex items-start justify-between mb-10">
                                        <div className="w-14 h-14 border border-gold/40 flex items-center justify-center group-hover:border-gold group-hover:bg-gold/10 transition-all">
                                            <Icon className="w-6 h-6 text-gold" />
                                        </div>
                                        <span className="font-display text-gold/50 text-2xl">{p.n}</span>
                                    </div>
                                    <h3 className="font-display text-white text-2xl md:text-3xl leading-tight mb-5">
                                        {t(`pillars.p${p.n.replace(/^0/, '')}_title`)}
                                    </h3>
                                    <p className="text-white/65 text-sm md:text-base leading-relaxed">
                                        {t(`pillars.p${p.n.replace(/^0/, '')}_desc`)}
                                    </p>
                                    <Link to="/strategy" className="mt-8 inline-flex items-center gap-2 text-gold text-[12px] tracking-widest uppercase link-underline">
                                        {t('common.learn_more')} <ChevronRight className="w-3.5 h-3.5" />
                                    </Link>
                                </StaggerItem>
                            );
                        })}
                    </Stagger>
                </div>
            </section>

            {/* PROJECTS CAROUSEL */}
            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                        <ScrollReveal>
                            <div className="max-w-2xl">
                                <div className="eyebrow mb-5">{t('home.projects_eyebrow')}</div>
                                <h2 className="editorial-h2 text-navy text-3xl md:text-5xl">
                                    {t('home.projects_h2')}
                                </h2>
                            </div>
                        </ScrollReveal>
                        <Link to="/projects" data-testid="link-see-all-projects" className="text-navy text-[12px] tracking-widest uppercase link-underline">
                            {t('common.see_all')} →
                        </Link>
                    </div>
                    <Carousel opts={{ align: 'start', loop: false }} data-testid="projects-carousel">
                        <CarouselContent className="-ml-4">
                            {PROJECTS.map((p) => (
                                <CarouselItem key={p.slug} className="pl-4 md:basis-1/2 lg:basis-1/3">
                                    <Link to="/projects" data-testid={`project-card-${p.slug}`} className="block bg-white border border-navy/10 hover:border-gold transition-colors p-8 h-full group">
                                        <div className="flex items-center justify-between mb-6">
                                            <span className="text-[11px] tracking-[0.2em] uppercase text-ink/60">{p.country}</span>
                                            <span className="text-[10px] tracking-widest uppercase px-2.5 py-1 bg-navy text-gold">{p.stage}</span>
                                        </div>
                                        <h3 className="font-display text-navy text-xl md:text-2xl leading-tight min-h-[3.5rem]">
                                            {p.name}
                                        </h3>
                                        <div className="hairline-gold my-6" />
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-[10px] tracking-widest uppercase text-ink/50">Commodity</div>
                                                <div className="text-ink text-sm mt-1">{p.commodity}</div>
                                            </div>
                                            <ArrowRight className="w-5 h-5 text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                                        </div>
                                    </Link>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <div className="flex items-center justify-end gap-3 mt-10">
                            <CarouselPrevious className="static translate-y-0 bg-navy text-gold border-gold/30 hover:bg-gold hover:text-navy" />
                            <CarouselNext className="static translate-y-0 bg-navy text-gold border-gold/30 hover:bg-gold hover:text-navy" />
                        </div>
                    </Carousel>
                </div>
            </section>

            {/* TIMELINE TEASER */}
            <section className="bg-white py-24 md:py-32 border-y border-navy/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="max-w-2xl">
                            <div className="eyebrow mb-5">{t('home.timeline_eyebrow')}</div>
                            <h2 className="editorial-h2 text-navy text-3xl md:text-5xl">
                                {t('home.timeline_h2')}
                            </h2>
                        </div>
                    </ScrollReveal>
                    <div className="mt-16 relative">
                        <div className="absolute left-0 right-0 top-1/2 h-px bg-gold/30" />
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-8 relative">
                            {[MILESTONES[0], MILESTONES[3], MILESTONES[5], MILESTONES[7], MILESTONES[10], MILESTONES[11]].map((m, i) => (
                                <ScrollReveal key={m.year} delay={i * 0.06}>
                                    <div className="flex flex-col items-center text-center px-3">
                                        <div className="metric-number text-2xl md:text-3xl mb-2">{m.year}</div>
                                        <div className="w-3 h-3 rounded-full bg-gold border-2 border-white shadow my-2" />
                                        <div className="text-ink/70 text-[11px] tracking-wide leading-snug max-w-[140px]">
                                            {m.title}
                                        </div>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                    <div className="mt-14 text-center">
                        <Link to="/history" data-testid="link-full-history" className="inline-flex items-center gap-2 text-navy text-[12px] tracking-widest uppercase link-underline">
                            {t('common.see_full_history')} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* PARTNERS STRIP */}
            <section className="bg-cream py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <div className="eyebrow mb-4">{t('home.partners_eyebrow')}</div>
                            <h3 className="editorial-h2 text-navy text-2xl md:text-3xl">
                                {t('home.partners_h2')}
                            </h3>
                        </div>
                    </ScrollReveal>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-navy/10 border border-navy/10">
                        {[...PARTNERS.historical.slice(0, 5), ...PARTNERS.active].slice(0, 10).map((p) => (
                            <div key={p.name} className="bg-cream py-8 px-4 text-center hover:bg-white transition-colors">
                                <div className="font-display text-navy text-base md:text-lg tracking-tight">{p.name}</div>
                                <div className="text-ink/50 text-[10px] tracking-widest uppercase mt-2">{p.period}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <Link to="/partnerships" data-testid="link-see-all-partners" className="text-navy text-[12px] tracking-widest uppercase link-underline">
                            {t('common.see_all')} →
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA BANNER */}
            <section className="relative py-24 md:py-32 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={ASSETS.desertPipeline} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-navy-deep/55" />
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-navy-deep/70 to-transparent" />
                </div>
                <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    <ScrollReveal>
                        <h2 className="editorial-h2 text-white text-3xl md:text-5xl">
                            {t('home.cta_h2')}
                        </h2>
                        <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                            {t('home.cta_sub')}
                        </p>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="cta-get-in-touch"
                                className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-navy-deep text-[13px] tracking-widest uppercase font-medium hover:bg-gold-light transition-colors"
                            >
                                {t('common.get_in_touch')} <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/contact" className="text-gold text-[12px] tracking-widest uppercase link-underline">
                                {t('common.view_contact')} →
                            </Link>
                        </div>
                    </ScrollReveal>
                </div>
            </section>
        </div>
    );
};

export default Home;
